import React, { useState, useRef } from 'react';
import { shareReportAsPdf, downloadReportAsPdf } from '../../../utils/reportPdfShare';
import { CheckCircle2, XCircle, ShieldCheck, Share2, Download, Users, Link2 } from 'lucide-react';

// ── SugarHathi brand palette (matches GRAD in do_finance_new.jsx) ─────────
const ACCENT = '#f97316';
const ACCENT_DARK = '#ea580c';
const BG = '#fffbf5';
const INK = '#1f2937';
const SLATE = '#6b7280';
const LINE = '#f0e6d8';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const GREEN = '#16a34a';
const RED = '#dc2626';

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// "12/10/2021" -> "12 Oct 2021"
function formatDate(mmddyyyy) {
  if (!mmddyyyy) return null;
  const parts = mmddyyyy.split('/');
  if (parts.length !== 3) return mmddyyyy;
  const [a, b, y] = parts;
  // CIN payload uses MM/DD/YYYY per sample (dateOfIncorporation: 12/10/2021, dateOfLastAGM: 09/30/2025)
  const mi = parseInt(a, 10) - 1;
  if (mi < 0 || mi > 11) return mmddyyyy;
  return `${parseInt(b, 10)} ${MONTH_SHORT[mi]} ${y}`;
}
function formatMoney(v) {
  const n = Number(v);
  if (!n) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

// CIN only exists for entities registered with the Ministry of Corporate
// Affairs (Companies, LLPs). Proprietorships and Partnerships never have
// one — this isn't a failed lookup, it's an expected non-applicable state.
const NON_COMPANY_CONSTITUTIONS = ['Proprietorship', 'Partnership'];

// ── CIN Verification sample response (Decentro) ────────────────────────────
const SAMPLE = {
  status: 'SUCCESS',
  // Sourced from the applicant's GSTIN Detailed constitutionOfBusiness in
  // the real pipeline — decides whether CIN lookup even applies.
  entityConstitution: 'Private Limited Company',
  kycResult: {
    companyData: {
      cIN: 'U52609TG2021PTC157830',
      company: 'VILLGRAINS FARMTECH PRIVATE LIMITED',
      registrationNumber: '157830',
      dateOfIncorporation: '12/10/2021',
      emailAddress: 'villagegrains@gmail.com',
      companyCategory: 'Company limited by shares',
      companySubcategory: 'Non-government company',
      classOfCompany: 'Private',
      authorisedCapital: '40000000',
      paidUpCapital: '11745150',
      registeredAddress: '101B, Door No. 8-2-248/B/8/101, 1st Floor, Acharya Nivas, Journalist Colony, Road No. 3, Banjara Hills, Banjara Hills, Hyderabad, Khairatabad, Telangana, India, 500034',
      dateOfLastAGM: '09/30/2025',
      balanceSheetDate: '03/31/2025',
      companyStatus: 'Active',
      rocName: 'ROC Hyderabad',
      whetherListedOrNot: 'N',
    },
    directorData: [
      { dIN: '03599212', firstName: 'KRISHNA', middleName: 'KUMAR', lastName: 'MENA', dateOfAppointment: '12/10/2021', directorDisqualified: '' },
      { dIN: '07680079', firstName: 'LOKA', middleName: 'KUMAR', lastName: 'AJAY', dateOfAppointment: '12/10/2021', directorDisqualified: '' },
      { dIN: '07034072', firstName: 'KAKUMANU', middleName: 'MAPATHI', lastName: 'REDDY', dateOfAppointment: '12/10/2021', directorDisqualified: '' },
      { dIN: '07359814', firstName: 'SARSAN', middleName: 'REDDY', lastName: 'SRIDHAR', dateOfAppointment: '12/10/2021', directorDisqualified: '' },
      { dIN: '02585802', firstName: 'HANUMA', middleName: 'SASTRY', lastName: 'GORINTLA', dateOfAppointment: '12/01/2022', directorDisqualified: '' },
      { dIN: '05166079', firstName: 'ESWARA', middleName: 'REDDY', lastName: 'DOMMETI', dateOfAppointment: '02/20/2026', directorDisqualified: '' },
    ],
    indexChargesData: [
      {
        sRN: 'AC4674522', chargeId: '101332192', amount: '82500000',
        dateOfCreation: '06/29/2026', chargeStatus: 'OPEN', chName: 'Others',
        streetAddress: 'D NO 4-1-427, 1st FLOOR, BANK STREET, TROOP BAZAR, Hyderabad, Hyderabad, Telangana India 500001',
      },
    ],
  },
  decentroTxnId: 'D4853372F227400592B2A77FE7BF1DC0',
  responseCode: 'S00000',
  requestTimestamp: '2026-08-08 17:40:56.885810 IST (GMT +0530)',
  responseTimestamp: '2026-08-08 17:40:56.946413 IST (GMT +0530)',
};

function StatusPill({ label, ok }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 700, background: ok ? '#f0fdf4' : '#fef2f2',
      color: ok ? GREEN : RED, border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}`,
    }}>
      {ok ? <CheckCircle2 size={12} strokeWidth={2.75} /> : <XCircle size={12} strokeWidth={2.75} />}
      {label}
    </span>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '6px 0', borderBottom: `1px solid ${LINE}` }}>
      <span style={{ fontSize: 12, color: SLATE, fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: INK, fontWeight: 600, textAlign: 'right', fontFamily: mono ? MONO : 'inherit', wordBreak: 'break-word' }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, footer }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 8 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, color: ACCENT_DARK, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
        {Icon && <Icon size={13} />}
        {title}
      </h3>
      <div>{children}</div>
      {footer && <div style={{ paddingTop: 10, marginTop: 2 }}>{footer}</div>}
    </div>
  );
}

// Compact per-director card row (name + DIN + appointment date + disqualified flag)
function DirectorRow({ d }) {
  const name = [d.firstName, d.middleName, d.lastName].filter(Boolean).join(' ');
  const disqualified = (d.directorDisqualified || '').trim().length > 0;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${LINE}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, wordBreak: 'break-word' }}>{name}</div>
        <div style={{ fontSize: 10.5, color: SLATE, fontFamily: MONO, marginTop: 1 }}>
          DIN {d.dIN} · Appt. {formatDate(d.dateOfAppointment)}
        </div>
      </div>
      {disqualified
        ? <StatusPill label="Disqualified" ok={false} />
        : <StatusPill label="Active" ok={true} />}
    </div>
  );
}

function ChargeRow({ c }) {
  const open = c.chargeStatus === 'OPEN';
  return (
    <div style={{ padding: '8px 0', borderBottom: `1px solid ${LINE}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>{formatMoney(c.amount)}</div>
        <StatusPill label={c.chargeStatus} ok={!open} />
      </div>
      <div style={{ fontSize: 10.5, color: SLATE }}>
        {c.chName} · Created {formatDate(c.dateOfCreation)} · SRN {c.sRN}
      </div>
    </div>
  );
}

function NotApplicableCard({ constitution, entityName }) {
  return (
    <div style={{ minHeight: '100%', background: BG, padding: '20px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ maxWidth: 580, width: '100%', margin: '0 auto' }}>
        <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`, borderRadius: 18, padding: '18px 20px', position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <ShieldCheck size={15} color="#fff" strokeWidth={2.5} />
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                CIN Verification · Credit Report
              </span>
            </div>
            {entityName && <h1 style={{ color: '#fff', fontSize: 16, fontWeight: 800, margin: 0, wordBreak: 'break-word' }}>{entityName}</h1>}
          </div>
        </div>
        <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '20px 18px', textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: BG, margin: '0 auto 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <XCircle size={20} color={SLATE} strokeWidth={2} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 6 }}>Not Applicable</div>
          <p style={{ fontSize: 12.5, color: SLATE, lineHeight: 1.6, margin: 0, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
            This applicant is registered as a <strong>{constitution}</strong>. CIN (Corporate Identification Number)
            applies only to Companies and LLPs registered with the Ministry of Corporate Affairs — {constitution?.toLowerCase()}s
            aren't assigned one, so this check doesn't apply here.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function CinAdminReport({ data = SAMPLE }) {
  const cardRef = useRef(null);
  if (NON_COMPANY_CONSTITUTIONS.includes(data.entityConstitution)) {
    return <NotApplicableCard constitution={data.entityConstitution} entityName={data.entityName} />;
  }

  const cd = data.kycResult.companyData;
  const directors = data.kycResult.directorData || [];
  const charges = data.kycResult.indexChargesData || [];

  // Dedup directors by DIN (raw data can repeat a director across
  // appointment/reappointment events) — show the earliest appointment date.
  const uniqueDirectors = Object.values(
    directors.reduce((acc, d) => {
      const key = d.dIN;
      if (!acc[key] || new Date(d.dateOfAppointment) < new Date(acc[key].dateOfAppointment)) {
        acc[key] = d;
      }
      return acc;
    }, {})
  );

  const openCharges = charges.filter(c => c.chargeStatus === 'OPEN');
  const totalOpenChargeAmount = openCharges.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadReportAsPdf(cardRef, 'cin-verification-admin-report.pdf', BG); } finally { setPdfBusy(false); }
  };
  const handleShare = async () => {
    const shareText = `CIN Verification — ${cd.company}\nCIN: ${cd.cIN}\nStatus: ${cd.companyStatus}\nDirectors: ${uniqueDirectors.length} · Open Charges: ${openCharges.length}`;
    setPdfBusy(true);
    try { await shareReportAsPdf(cardRef, 'cin-verification-admin-report.pdf', 'CIN Verification — Admin Report', shareText, BG); } finally { setPdfBusy(false); }
  };

  return (
    <div style={{ minHeight: '100%', background: BG, padding: '20px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff !important; } }
      `}</style>
      <div ref={cardRef} style={{ maxWidth: 580, width: '100%', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={handleShare} disabled={pdfBusy} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px', color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <Share2 size={13} /> Share
          </button>
          <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px', color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <Download size={13} /> Download as PDF
          </button>
        </div>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`, borderRadius: 18, padding: '18px 20px', position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{
            position: 'absolute', top: 18, right: 20, zIndex: 1,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 9,
              padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <CheckCircle2 size={11} color="#fff" strokeWidth={3} />
              <span style={{ color: '#fff', fontSize: 10.5, fontWeight: 800 }}>{cd.companyStatus}</span>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, fontFamily: MONO, whiteSpace: 'nowrap' }}>
              Incorp: {formatDate(cd.dateOfIncorporation)}
            </span>
          </div>
          <div style={{ position: 'relative', paddingRight: 110 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <ShieldCheck size={15} color="#fff" strokeWidth={2.5} />
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                CIN Verification · Credit Report
              </span>
            </div>
            <h1 style={{ color: '#fff', fontSize: 16, fontWeight: 800, margin: 0, wordBreak: 'break-word' }}>{cd.company}</h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, margin: '2px 0 0', fontFamily: MONO }}>
              {cd.cIN}
            </p>
          </div>
        </div>

        {/* Company Details */}
        <SectionCard title="Company Details">
          <Row label="Registration Number" value={cd.registrationNumber} mono />
          <Row label="ROC" value={cd.rocName} />
          <Row label="Category" value={cd.companyCategory} />
          <Row label="Subcategory" value={cd.companySubcategory} />
          <Row label="Class of Company" value={cd.classOfCompany} />
          <Row label="Listed" value={cd.whetherListedOrNot === 'Y' ? 'Yes' : 'No'} />
          <Row label="Email" value={cd.emailAddress} mono />
          <Row label="Registered Address" value={cd.registeredAddress} />
        </SectionCard>

        {/* Capital & Filings */}
        <SectionCard title="Capital & Filing Dates">
          <Row label="Authorised Capital" value={formatMoney(cd.authorisedCapital)} />
          <Row label="Paid-up Capital" value={formatMoney(cd.paidUpCapital)} />
          <Row label="Date of Last AGM" value={formatDate(cd.dateOfLastAGM)} />
          <Row label="Balance Sheet Date" value={formatDate(cd.balanceSheetDate)} />
        </SectionCard>

        {/* Directors */}
        <SectionCard
          title={`Directors (${uniqueDirectors.length})`}
          icon={Users}
        >
          {uniqueDirectors.map((d, i) => <DirectorRow key={d.dIN || i} d={d} />)}
        </SectionCard>

        {/* Charges / Liens */}
        <SectionCard
          title={`Charges & Liens (${charges.length})`}
          icon={Link2}
          footer={
            charges.length > 0 ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatusPill label={`${openCharges.length} open`} ok={openCharges.length === 0} />
                {openCharges.length > 0 && <StatusPill label={`${formatMoney(totalOpenChargeAmount)} secured`} ok={false} />}
              </div>
            ) : null
          }
        >
          {charges.length > 0
            ? charges.map((c, i) => <ChargeRow key={c.chargeId || i} c={c} />)
            : <p style={{ fontSize: 12, color: SLATE, margin: 0 }}>No registered charges found — company's assets are unencumbered as per MCA records.</p>}
        </SectionCard>

      </div>
    </div>
  );
}
